import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ProofOfOwnership } from '@volkswagen-onehub/icons-core';
import { Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';

gsap.registerPlugin(ScrollToPlugin);

const COMPACT_SCROLL_THRESHOLD = 0;

const NbabGti50Container = styled.button<{ $isCompact: boolean }>`
  position: fixed;
  right: clamp(16px, 4.16vw, 106px);
  bottom: 58px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $isCompact }) => ($isCompact ? '0px' : '8px')};
  min-width: ${({ $isCompact }) => ($isCompact ? '64px' : 'auto')};
  min-height: 64px;
  padding: ${({ $isCompact }) => ($isCompact ? '12px' : '12px 28px')};
  border: none;
  border-radius: 999px;
  background: #c90010;
  color: #fff;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
  transition:
    min-width 0.28s ease,
    padding 0.28s ease,
    gap 0.28s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:focus-visible {
    box-shadow:
      0 12px 30px rgba(0, 0, 0, 0.22),
      0 0 0 4px rgba(0, 149, 255, 0.4);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      min-width: auto;
      padding: 12px 28px;
      gap: 8px;
      transform: translateY(-2px);
      box-shadow: 0 16px 34px rgba(0, 0, 0, 0.24);
    }
  }

  @media (max-width: 767px) {
    right: 16px;
    bottom: 24px;
    min-width: ${({ $isCompact }) => ($isCompact ? '60px' : 'auto')};
    min-height: 60px;
    padding: ${({ $isCompact }) => ($isCompact ? '10px' : '10px 22px')};

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        min-width: auto;
        padding: 10px 22px;
      }
    }
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  color: inherit;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const Label = styled(Text)<{ $isCompact: boolean }>`
  line-height: 1.05;
  letter-spacing: 0.01em;
  white-space: nowrap;
  color: #fff;
  opacity: ${({ $isCompact }) => ($isCompact ? 0 : 1)};
  max-width: ${({ $isCompact }) => ($isCompact ? '0px' : '190px')};
  filter: blur(${({ $isCompact }) => ($isCompact ? '4px' : '0px')});
  transform: translateX(${({ $isCompact }) => ($isCompact ? '-12px' : '0px')});
  transition:
    opacity 0.22s ease,
    filter 0.22s ease,
    max-width 0.34s ease,
    transform 0.34s ease;

  @media (hover: hover) and (pointer: fine) {
    ${NbabGti50Container}:hover & {
      opacity: 1;
      filter: blur(0px);
      max-width: 190px;
      transform: translateX(0);
    }
  }
`;

const highlightArrival = (el: HTMLElement) => {
  gsap.fromTo(
    el,
    { outline: '2px solid rgba(0,0,0,0)', outlineOffset: 6 },
    {
      outline: '2px solid rgba(0,0,0,0.25)',
      outlineOffset: 2,
      duration: 0.2,
      yoyo: true,
      repeat: 1,
      ease: 'power1.out',
      clearProps: 'outline,outlineOffset',
      delay: 0.05,
    },
  );
};

export const NbabGti50 = (): React.ReactElement => {
  const isAnimatingRef = useRef(false);
  const hasInitialScrolledRef = useRef(false);
  const [isCompact, setIsCompact] = useState(false);

  const updateCompactState = () => {
    setIsCompact(window.scrollY > COMPACT_SCROLL_THRESHOLD);
  };

  const scrollToTarget = () => {
    const target = document.querySelector('#formulario') as HTMLElement | null;
    if (!target || isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOSSafari) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isAnimatingRef.current = false;
        highlightArrival(target);
      }, 900);
    } else {
      try {
        const offsetY = 0;
        gsap.to(window, {
          duration: 2.9,
          scrollTo: { y: target, offsetY, autoKill: true },
          ease: 'power2.out',
          onComplete: () => {
            isAnimatingRef.current = false;
            highlightArrival(target);
          },
          onInterrupt: () => {
            isAnimatingRef.current = false;
          },
        });
      } catch {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          isAnimatingRef.current = false;
          highlightArrival(target);
        }, 900);
      }
    }
  };

  const handleClickNbabGti50 = (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToTarget();
  };

  useEffect(() => {
    updateCompactState();
    window.addEventListener('scroll', updateCompactState, { passive: true });

    if (!hasInitialScrolledRef.current && window.location.hash === '#formulario') {
      hasInitialScrolledRef.current = true;
      setTimeout(() => scrollToTarget(), 500);
    }

    return () => {
      window.removeEventListener('scroll', updateCompactState);
      gsap.killTweensOf(window);
    };
  }, []);

  return (
    <NbabGti50Container
      $isCompact={isCompact}
      onClick={handleClickNbabGti50}
      aria-label="Ir al formulario"
      title="Ir al formulario"
    >
      <IconWrap>
        <ProofOfOwnership variant="default" aria-hidden />
      </IconWrap>
      <Label $isCompact={isCompact} appearance={TokenTextAppearance.copy200} bold>
        Consíguelo
      </Label>
    </NbabGti50Container>
  );
};
